import type {
  BatchLookupResult,
  ClientForBatch,
  ClientScreening,
  ClientViewSummary,
  ComplianceClientRow,
  ComplianceFlowRow,
  CompliancePeriod,
  ComplianceQueueItem,
  ComplianceReviewLogResponse,
  IdentityChangesResponse,
  RecordComplianceCheckRequest,
  RecordComplianceChecksRequest,
  RecordComplianceChecksResponse,
  RecordReviewRequest,
} from "@repo/shared/types";
import { get, patch, post } from "@/lib/api/_client";
import { browserTimezone } from "./browser-timezone";

const BASE = "/internal/compliance";

export function getApprovalQueue(): Promise<ComplianceQueueItem[]> {
  return get(`${BASE}/approvals`);
}

export function getViews(): Promise<ClientViewSummary[]> {
  return get(`${BASE}/views`);
}

export function getClientsForView(viewId: string): Promise<ClientForBatch[]> {
  return post(`${BASE}/views/${viewId}/clients?timezone=${encodeURIComponent(browserTimezone())}`);
}

export function runBatchLookup(
  clients: ClientForBatch[],
): Promise<{ results: BatchLookupResult[]; isTestMode: boolean }> {
  return post(`${BASE}/batch-lookup`, { clients });
}

export function getClientsForReview(viewId: string): Promise<ComplianceClientRow[]> {
  return get(
    `${BASE}/review/clients?viewId=${encodeURIComponent(viewId)}&timezone=${encodeURIComponent(browserTimezone())}`,
  );
}

export function getFlowsForReview(period: CompliancePeriod): Promise<ComplianceFlowRow[]> {
  // The calendar periods (this month / quarter / year) start in the reviewer's own timezone.
  return get(
    `${BASE}/review/flows?period=${period}&timezone=${encodeURIComponent(browserTimezone())}`,
  );
}

export function recordReview(
  clientId: string,
  body: RecordReviewRequest,
): Promise<{ success: true }> {
  return post(`${BASE}/clients/${clientId}/review`, body satisfies RecordReviewRequest);
}

/**
 * QL-b — the screening log for one client, newest first.
 *
 * The review dialog needs it because `ComplianceClientRow` carries only the
 * cached findings, not the matched individuals a reviewer has to adjudicate
 * one by one.
 */
export function getClientScreenings(clientId: string): Promise<ClientScreening[]> {
  return get(`${BASE}/clients/${clientId}/screenings`);
}

/** QL-f — recent identity changes across the tenant, newest first. */
export function getIdentityChanges(limit?: number) {
  const query = limit ? `?limit=${limit}` : "";
  return get<IdentityChangesResponse>(`/internal/compliance/identity-changes${query}`);
}

/** One client's identity history. */
export function getClientIdentityChanges(clientId: string) {
  return get<IdentityChangesResponse>(`/internal/compliance/clients/${clientId}/identity-changes`);
}

// ── Compliance checks on completed advice ────────────────────────────────────
// Nothing here touches the in-process approval gate (`getApprovalQueue` above).

/** Record a compliance-check verdict on one completed flow instance. */
export function recordComplianceCheck(
  flowInstanceId: string,
  body: RecordComplianceCheckRequest,
): Promise<{ success: true }> {
  return patch(
    `${BASE}/flows/${flowInstanceId}/check`,
    body satisfies RecordComplianceCheckRequest,
  );
}

/** The compliance-review history of one flow instance, newest first. */
export function getComplianceReviewLog(
  flowInstanceId: string,
): Promise<ComplianceReviewLogResponse> {
  return get(`${BASE}/flows/${flowInstanceId}/review-log`);
}

/** Record the same verdict on several completed flow instances at once. */
export function recordComplianceChecks(
  body: RecordComplianceChecksRequest,
): Promise<RecordComplianceChecksResponse> {
  return patch(`${BASE}/flows/check`, body satisfies RecordComplianceChecksRequest);
}
